import { useState, useMemo } from 'react';
import { List, Show, Create, Edit, SimpleForm, TextInput, SelectInput, FileInput, FileField, Datagrid, TextField, DateField, ChipField, useGetList, useNavigate, useRecordContext, useRefresh, useNotify, useUpdate, ReferenceInput } from 'react-admin';
import { Chip, Box, Typography, Paper, Grid, Button, IconButton, Alert, Divider, Table, TableBody, TableCell, TableHead, TableRow, Avatar, LinearProgress } from '@mui/material';
import type { ChipColor, ProgressColor } from '@/types/chip';
import { Description, CheckCircle, Cancel, Schedule, CloudUpload, Delete, Visibility, Close, Warning, VerifiedUser, Pending, Search, FilterList, Add, Edit as EditIcon, FolderOpen } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { ListPageHeader } from '@/components/ListPageHeader';
import { useEmployeeMap, getEmployeeName } from '@/hooks/useEmployeeMap';

interface StatutoryDocumentItem {
  id: string;
  documentType: string;
  status: string;
  employee?: { id: string };
  employeeId?: string;
  documentId?: string;
  uploadedAt?: string;
  verifiedAt?: string;
}

interface EmployeeSuggestionItem {
  legalEntity?: { firstName?: string; lastName?: string };
}

const DOCUMENT_TYPES = ['RSAID', 'Passport', 'TaxNumber', 'BankDetails', 'WorkPermit', 'PoliceClearance', 'CreditCheck'] as const;
const DOCUMENT_STATUSES = ['Pending', 'Uploaded', 'Verified', 'Rejected'] as const;

const STATUS_COLORS: Record<string, string> = {
  Pending: 'warning',
  Uploaded: 'info',
  Verified: 'success',
  Rejected: 'error',
};

const STATUS_ICONS: Record<string, React.ReactElement> = {
  Pending: <Schedule sx={{ fontSize: 16 }} />,
  Uploaded: <CloudUpload sx={{ fontSize: 16 }} />,
  Verified: <CheckCircle sx={{ fontSize: 16 }} />,
  Rejected: <Cancel sx={{ fontSize: 16 }} />,
};

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  RSAID: 'RSA Identity Document',
  Passport: 'Passport',
  TaxNumber: 'Tax Number (IRP5)',
  BankDetails: 'Bank Details Confirmation',
  WorkPermit: 'Work Permit/Visa',
  PoliceClearance: 'Police Clearance Certificate',
  CreditCheck: 'Credit Check',
};

export const StatutoryDocumentList = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { data: documents, isLoading } = useGetList('statutory-documents', { pagination: { page: page + 1, perPage: rowsPerPage } });
  const [filterType, setFilterType] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [searchText, setSearchText] = useState('');
  const employeeMap = useEmployeeMap();

  const filteredDocuments = useMemo(() => {
    if (!documents) return [];
    return documents.filter((doc: StatutoryDocumentItem) => {
      if (filterType && doc.documentType !== filterType) return false;
      if (filterStatus && doc.status !== filterStatus) return false;
      if (searchText) {
        const search = searchText.toLowerCase();
        const empName = getEmployeeName(employeeMap, doc.employee?.id).toLowerCase();
        if (!empName.includes(search) &&
            !doc.documentId?.toLowerCase().includes(search)) return false;
      }
      return true;
    });
  }, [documents, filterType, filterStatus, searchText, employeeMap]);

  const groupedByEmployee = useMemo(() => {
    const groups: Record<string, StatutoryDocumentItem[]> = {};
    filteredDocuments.forEach((doc: StatutoryDocumentItem) => {
      const key = doc.employeeId || 'unknown';
      if (!groups[key]) groups[key] = [];
      groups[key].push(doc);
    });
    return groups;
  }, [filteredDocuments]);

  const stats = {
    total: documents?.length ?? 0,
    verified: documents?.filter((d: StatutoryDocumentItem) => d.status === 'Verified').length ?? 0,
    pending: documents?.filter((d: StatutoryDocumentItem) => d.status === 'Pending' || d.status === 'Uploaded').length ?? 0,
    rejected: documents?.filter((d: StatutoryDocumentItem) => d.status === 'Rejected').length ?? 0,
  };

  return (
    <Box sx={{ p: 2 }}>
      <ListPageHeader
        title="Statutory Documents"
        metrics={[
          { label: 'Verified', value: String(stats.verified), color: 'success.main' },
          { label: 'Pending', value: String(stats.pending), color: 'warning.main' },
          { label: 'Rejected', value: String(stats.rejected), color: 'error.main' },
          { label: 'Total', value: String(stats.total), color: 'primary.main' },
        ]}
      />

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextInput
            source="search"
            placeholder="Search by employee or document ID..."
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)}
            sx={{ minWidth: 300 }}
          />
          <SelectInput
            source="documentType"
            choices={[{ id: '', name: 'All Types' }, ...DOCUMENT_TYPES.map(t => ({ id: t, name: DOCUMENT_TYPE_LABELS[t] || t }))]}
            onChange={(e) => { const ev = e as React.ChangeEvent<HTMLInputElement>; setFilterType(ev.target.value); }}
            sx={{ minWidth: 200 }}
          />
          <SelectInput
            source="status"
            choices={[{ id: '', name: 'All Status' }, ...DOCUMENT_STATUSES.map(s => ({ id: s, name: s }))]}
            onChange={(e) => { const ev = e as React.ChangeEvent<HTMLInputElement>; setFilterStatus(ev.target.value); }}
            sx={{ minWidth: 150 }}
          />
          <Button
            component={Link}
            to="/statutory-documents/create"
            variant="contained"
            startIcon={<Add />}
          >
            Upload Document
          </Button>
        </Box>
      </Paper>

      {isLoading ? (
        <LinearProgress />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {(Object.entries(groupedByEmployee) as [string, StatutoryDocumentItem[]][]).map(([employeeId, docs]) => (
            <Paper key={employeeId} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
                  {getEmployeeName(employeeMap, docs[0]?.employee?.id)[0] || '?'}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {getEmployeeName(employeeMap, docs[0]?.employee?.id) || 'Unknown Employee'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {docs.length} document{docs.length !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              </Box>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Document Type</TableCell>
                    <TableCell>Document ID</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Uploaded</TableCell>
                    <TableCell>Verified</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {docs.map((doc: StatutoryDocumentItem) => (
                    <TableRow key={doc.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Description fontSize="small" color="action" />
                          <Typography variant="body2">
                            {DOCUMENT_TYPE_LABELS[doc.documentType] || doc.documentType}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {doc.documentId || doc.id.split(':')[1]?.substring(0, 8).toUpperCase()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={STATUS_ICONS[doc.status]}
                          label={doc.status}
                          size="small"
                          color={STATUS_COLORS[doc.status] as ChipColor}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString('en-GB') : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {doc.verifiedAt ? new Date(doc.verifiedAt).toLocaleDateString('en-GB') : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            component={Link}
                            to={`/statutory-documents/${doc.id}/show`}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            component={Link}
                            to={`/statutory-documents/${doc.id}/edit`}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          ))}

          {filteredDocuments.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <FolderOpen sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">No statutory documents found</Typography>
            </Paper>
          )}
        </Box>
      )}
    </Box>
  );
};

export const StatutoryDocumentShow = () => {
  const record = useRecordContext();
  const navigate = useNavigate();
  const [update, { isLoading: isUpdating }] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();

  if (!record) return null;

  const isPending = record.status === 'Pending';
  const isRejected = record.status === 'Rejected';
  const isVerified = record.status === 'Verified';
  const isUploaded = record.status === 'Uploaded';

  const handleVerify = async () => {
    try {
      await update('statutory-documents', {
        id: record.id,
        data: { status: 'Verified', verifiedAt: new Date().toISOString(), verifiedBy: 'Current User' },
      });
      notify('Document verified successfully', { type: 'success' });
      refresh();
    } catch {
      notify('Failed to verify document', { type: 'error' });
    }
  };

  const handleReject = async () => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;
    try {
      await update('statutory-documents', {
        id: record.id,
        data: { status: 'Rejected', rejectionReason: reason },
      });
      notify('Document rejected', { type: 'warning' });
      refresh();
    } catch {
      notify('Failed to reject document', { type: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
            {record.employeeName?.[0] || '?'}
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {record.documentType}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {DOCUMENT_TYPE_LABELS[record.documentType] || record.documentType}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={() => navigate('/statutory-documents')}>
          <Close />
        </IconButton>
      </Box>

      {isPending && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<Schedule />}>
          Document pending verification. Please review and verify or reject.
        </Alert>
      )}
      {isRejected && (
        <Alert severity="error" sx={{ mb: 2 }} icon={<Cancel />}>
          Document rejected: {record.rejectionReason}
        </Alert>
      )}
      {isVerified && (
        <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>
          Document verified on {record.verifiedAt ? new Date(record.verifiedAt).toLocaleDateString('en-GB') : '—'}
        </Alert>
      )}
      {isUploaded && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<CloudUpload />}>
          Document uploaded, awaiting verification
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Document ID', value: record.documentId },
          { label: 'Employee', value: record.employeeName },
          { label: 'Employee ID', value: record.employeeNumber || record.employeeId },
          { label: 'Document Type', value: DOCUMENT_TYPE_LABELS[record.documentType] || record.documentType },
          { label: 'Status', value: record.status, chip: true, color: STATUS_COLORS[record.status] },
          { label: 'Uploaded At', value: record.uploadedAt ? new Date(record.uploadedAt).toLocaleString('en-GB') : '—' },
          { label: 'Uploaded By', value: record.uploadedBy || '—' },
          { label: 'Verified At', value: record.verifiedAt ? new Date(record.verifiedAt).toLocaleString('en-GB') : '—' },
          { label: 'Verified By', value: record.verifiedBy || '—' },
          { label: 'Rejection Reason', value: record.rejectionReason || '—' },
        ].map((field) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={field.label}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">{field.label}</Typography>
              {field.chip ? (
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={field.value} size="small" color={field.color as ChipColor} />
                </Box>
              ) : (
                <Typography variant="body1" sx={{ mt: 0.5 }}>{field.value}</Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>FICA Compliance Notice</Typography>
        <Typography variant="body2" color="text.secondary">
          According to FICA requirements, the RSA Identity Document must be verified before
          an employee can commence work. Tax number must also be submitted to SARS within
          the prescribed timeframe.
        </Typography>
      </Paper>

      {(isPending || isUploaded) && (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircle />}
            onClick={handleVerify}
          >
            Verify Document
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<Cancel />}
            onClick={handleReject}
          >
            Reject Document
          </Button>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button component={Link} to="/employees" variant="outlined" startIcon={<Description />}>
          View Employee
        </Button>
        <Button component={Link} to="/statutory-documents" variant="outlined" onClick={() => navigate('/statutory-documents')}>
          All Documents
        </Button>
      </Box>
    </Box>
  );
};

export const StatutoryDocumentCreate = () => {
  return (
    <Create>
      <SimpleForm>
        <Typography variant="h6" sx={{ mb: 2 }}>Upload Statutory Document</Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <ReferenceInput source="employee.id" reference="employees" fullWidth>
            <SelectInput optionText="name" label="Employee" />
          </ReferenceInput>

          <SelectInput
            source="documentType"
            choices={DOCUMENT_TYPES.map(t => ({ id: t, name: DOCUMENT_TYPE_LABELS[t] || t }))}
            fullWidth
          />

          <FileInput source="file" label="Document File" maxSize={10000000}>
            <FileField source="src" title="title" />
          </FileInput>

          <SelectInput
            source="status"
            choices={[{ id: 'Pending', name: 'Pending' }, { id: 'Uploaded', name: 'Uploaded' }]}
            defaultValue="Pending"
            fullWidth
          />

          <TextInput source="notes" label="Notes" multiline rows={3} fullWidth />

          <Alert severity="info">
            Allowed file types: PDF, JPG, PNG. Maximum file size: 10MB.
            FICA requires RSA ID to be verified before employee starts work.
          </Alert>
        </Box>
      </SimpleForm>
    </Create>
  );
};

export const StatutoryDocumentEdit = () => {
  const record = useRecordContext();
  const navigate = useNavigate();
  const [update, { isLoading: isUpdating }] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();

  const canEditStatus = record?.status === 'Pending' || record?.status === 'Uploaded' || record?.status === 'Rejected';

  return (
    <Edit>
      <SimpleForm>
        <Typography variant="h6" sx={{ mb: 2 }}>Edit Statutory Document</Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
          <TextInput source="documentId" label="Document ID" fullWidth disabled />
          <ReferenceInput source="employee.id" reference="employees" fullWidth>
            <SelectInput optionText="name" label="Employee" />
          </ReferenceInput>
          <SelectInput source="documentType" choices={DOCUMENT_TYPES.map(t => ({ id: t, name: DOCUMENT_TYPE_LABELS[t] || t }))} fullWidth />
          <SelectInput
            source="status"
            choices={DOCUMENT_STATUSES.map(s => ({ id: s, name: s }))}
            fullWidth
            disabled={!canEditStatus}
          />
          <TextInput source="uploadedBy" label="Uploaded By" fullWidth disabled />
          <TextInput source="verifiedBy" label="Verified By" fullWidth disabled />
        </Box>

        {record?.status === 'Rejected' && (
          <TextInput source="rejectionReason" label="Rejection Reason" fullWidth multiline rows={2} />
        )}

        {!canEditStatus && (
          <Alert severity="warning">
            Document status cannot be changed after verification. Contact system administrator for corrections.
          </Alert>
        )}
      </SimpleForm>
    </Edit>
  );
};